import { Effect, Layer, Option, Schema } from "effect";
import { HttpClient, HttpClientRequest, type HttpClientResponse } from "effect/unstable/http";
import { HttpApi, HttpApiClient } from "effect/unstable/httpapi";
import { StationApi as SharedStationApi } from "@workerstation/shared/station/api";
import { WorkspaceApi as SharedWorkspaceApi } from "@workerstation/shared/station/workspace-api";
import { StationId } from "@workerstation/shared/station-id";
import { WorkspaceId } from "@workerstation/shared/development-identifiers";
import { StationHttpError, stationInvalid } from "@workerstation/shared/errors/station";
import { WorkspaceHttpError, workspaceInvalid } from "@workerstation/shared/errors/workspace-http";
import { ResourceStorageError, resourceUnavailable } from "@workerstation/shared/errors/resource";
import { StationApi } from "./service.ts";
import type { StationClientFailure } from "./model.ts";

const Stations = HttpApi.make("FrontendStationApi").add(SharedStationApi);
const Workspaces = HttpApi.make("FrontendWorkspaceApi").add(SharedWorkspaceApi);
const Envelope = Schema.Struct({ error: Schema.optionalKey(Schema.Json) });
const Failures = Schema.Union([StationHttpError, WorkspaceHttpError, ResourceStorageError]);
const own = <A, E, R>(operation: Effect.Effect<A, E, R>) => Effect.scoped(operation.pipe(
  Effect.mapError((error): StationClientFailure => Schema.is(Failures)(error) ? error : resourceUnavailable())
));
const station = (value: string) => Schema.decodeUnknownEffect(StationId)(value).pipe(Effect.mapError(() => stationInvalid("Invalid workerstation id.")));
const workspace = (value: string) => Schema.decodeUnknownEffect(WorkspaceId)(value).pipe(Effect.mapError(() => workspaceInvalid("Invalid workerspace id.")));
// The collection exposes Top, while the endpoint retains its declared Type.
// All selected codecs are the exact pure shared schemas for this HTTP status.
const stationError = (status: number) => Schema.Union([...SharedStationApi.endpoints.read.error].filter(schema => schema.ast.annotations?.httpApiStatus === status)) as unknown as Schema.Codec<(typeof SharedStationApi.endpoints.read)["~Error"]["Type"], unknown>;
const workspaceError = (status: number) => Schema.Union([...SharedWorkspaceApi.endpoints.read.error].filter(schema => schema.ast.annotations?.httpApiStatus === status)) as unknown as Schema.Codec<(typeof SharedWorkspaceApi.endpoints.read)["~Error"]["Type"], unknown>;
const StationConflictFields = Schema.Struct({ currentUpdatedAt: StationHttpError.fields.currentUpdatedAt });
const publicRefusal = (response: HttpClientResponse.HttpClientResponse, stationResponse: boolean): Effect.Effect<never, StationClientFailure> => {
  const status = response.status;
  if (status !== 400 && status !== 404 && status !== 405 && status !== 409) return Effect.fail(resourceUnavailable());
  if (stationResponse) return response.json.pipe(
    Effect.flatMap(Schema.decodeUnknownEffect(stationError(status))), Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(body => Schema.decodeUnknownEffect(StationConflictFields)(body).pipe(
      Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(fields => Effect.fail(new StationHttpError({ status, message: body.error, ...fields })))
    ))
  );
  return response.json.pipe(
    Effect.flatMap(Schema.decodeUnknownEffect(workspaceError(status))), Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(({ error, ...fields }) => Effect.fail(new WorkspaceHttpError({ status, message: error, ...fields })))
  );
};

export const makeStationApi = Effect.fn("Frontend.StationApi.make")(function* (baseUrl: string) {
  const http = HttpClient.mapRequestEffect(HttpClient.withScope(yield* HttpClient.HttpClient), request => Effect.try({
    try: () => request.urlParams.params.length === 0 ? request : HttpClientRequest.make(request.method)(
      request.url + (request.url.includes("?") ? "&" : "?") + request.urlParams.params.map(([key, value]) => encodeURIComponent(key) + "=" + encodeURIComponent(value)).join("&"),
      { headers: request.headers, body: request.body, hash: Option.getOrUndefined(request.hash) }
    ),
    catch: () => resourceUnavailable()
  }));
  const json = (stationResponse: boolean) => HttpClient.transformResponse(http, response => response.pipe(
    Effect.mapError(() => resourceUnavailable()),
    Effect.flatMap(value => value.status >= 200 && value.status < 300 ? value.json.pipe(
      Effect.flatMap(Schema.decodeUnknownEffect(Envelope)), Effect.mapError(() => resourceUnavailable()),
      Effect.flatMap(body => body.error ? Effect.fail(resourceUnavailable()) : Effect.succeed(value))
    ) : publicRefusal(value, stationResponse))
  ));
  const stations = yield* HttpApiClient.makeWith(Stations, { httpClient: json(true), baseUrl });
  const workspaces = yield* HttpApiClient.makeWith(Workspaces, { httpClient: json(false), baseUrl });
  return StationApi.of({
    station: Effect.fn("Frontend.StationApi.station")(function* (stationId) {
      return yield* stations.station.read({ params: { stationId: yield* station(stationId) } });
    }, own),
    updateStation: Effect.fn("Frontend.StationApi.updateStation")(function* (stationId, payload) {
      return yield* stations.station.update({ params: { stationId: yield* station(stationId) }, payload });
    }, own),
    workspace: Effect.fn("Frontend.StationApi.workspace")(function* (workspaceId, stationHint) {
      return yield* workspaces.workspace.read({ params: { workspaceId: yield* workspace(workspaceId) }, query: stationHint === undefined ? {} : { station: stationHint } });
    }, own),
    updateWorkspace: Effect.fn("Frontend.StationApi.updateWorkspace")(function* (workspaceId, payload, stationHint) {
      return yield* workspaces.workspace.update({ params: { workspaceId: yield* workspace(workspaceId) }, query: stationHint === undefined ? {} : { station: stationHint }, payload });
    }, own)
  });
});
export const StationApiLive = (baseUrl: string) => Layer.effect(StationApi, makeStationApi(baseUrl));
